<script>
import {useRoute, useRouter} from "vue-router";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";
import {useI18n} from "vue-i18n";
import {useSubscriptionStore} from "../../../stores/subscription";
import {useOrganizationStore} from "../../../stores/organizations";
import BaseDataTable from "../../components/charts/BaseDataTable.vue";
import Icon from "../../components/Icon.vue";
import BaseModal from "../../components/BaseModal.vue";
import LoadingSpinner from "../../components/loadingSpinner.vue";
import Popup from "../../components/popup.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";

export default {
  name: "SubscriptionView",
  components: {Popup, LoadingSpinner, BaseModal, Icon, BaseDataTable},
  setup() {
    // Router
    const route = useRoute();
    const router = useRouter();

    // i18n
    const {t} = useI18n();

    const subscriptionStore = useSubscriptionStore();
    const organizationStore = useOrganizationStore();
    const analyticEventStore = useAnalyticEventStore();

    return {
      route,
      router,
      t,
      subscriptionStore,
      organizationStore,
      analyticEventStore,
    };
  },
  data() {
    return {
      saving: false,
      showWarningModal: false,
      cancellingSubscription: false,
      showPauseModal: false,
      pausingSubscription: false,
      showResumeModal: false,
      resumingSubscription: false,
      showStoppedModal: false,
      popupMessage: ""
    };
  },
  computed: {
    ApplicationHelper() {
      return ApplicationHelper;
    },
    subscription() {
      return this.subscriptionStore.currentSubscription;
    },
    donationData() {
      let labels = [
        {label: 'Amount', key: 'amount', format: 'money'},
        {label: 'Designation', key: 'target_name'},
        {label: 'Status', key: 'status'},
        {label: 'Date of Gift', key: 'paid_at', format: 'date'}
      ];
      if (!this.subscriptionStore.loadingRecord && this.subscription && this.subscription.latest_donations) {
        let rows = this.subscription.latest_donations.map((donation) => {
          donation.status = String(donation.status).charAt(0).toUpperCase() + String(donation.status).slice(1);
          return donation;
        });
        return {
          labels: labels,
          rows: rows
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },

    userFullName() {
      return this.subscription?.user?.first_name + " " + this.subscription?.user?.last_name;
    }
  },
  mounted() {
    this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
    this.analyticEventStore.logEvent({ action: 'subscription-view-detail' });
    this.subscriptionStore.getSubscription(this.route.params.id);
  },
  methods: {
    back() {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'subscription-back' });
      this.router.back();
    },
    navigateToDonorView(memberId) {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({
        action: `subscription-view-navigate-to-donor`,
        traceable_type: 'Member',
        traceable_id: memberId
      });
      this.router.push({name: 'client-dashboard', params: {id: memberId, org: this.route.params.org, panel: 'donors'}});
    },
    navigateToDonationView(donationId) {
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({
        action: `subscription-view-navigate-to-donation`,
        traceable_type: 'Donation',
        traceable_id: donationId
      });
      this.router.push({
        name: 'client-dashboard',
        params: {id: donationId, org: this.route.params.org, panel: 'gifts'}
      });
    },
    toggleEdit() {
      this.subscriptionStore.toggleEdit();
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: this.subscriptionStore.editing ? 'subscription-edit-start' : 'subscription-edit-cancel' });
    },
    async save() {
      await this.subscriptionStore.saveDonation();
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'subscription-edit-saved' });
    },
    cancelSubscription() {
      this.cancellingSubscription = true;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'subscription-cancelled' });
      this.subscriptionStore.cancelSubscription(this.subscription)
        .then((result) => {
          this.subscriptionStore.getSubscription(this.route.params.id);
          this.subscriptionStore.loadData();
          if (result) {
            this.showStoppedModal = true;
          } else {
            this.showPopup("Something went wrong");
          }
        })
          .catch(() => {
            this.showPopup("Something went wrong");
          })
          .finally(() => {
            this.cancellingSubscription = false;
            this.showWarningModal = false;
          });
    },
    pauseSubscription() {
      this.pausingSubscription = true;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'subscription-paused' });
      this.subscriptionStore.pauseSubscription(this.subscription)
        .then((result) => {
          this.subscriptionStore.getSubscription(this.route.params.id);
          this.subscriptionStore.loadData();
          this.showPopup(result ? "Subscription Paused!" : "Something went wrong");
        })
        .catch(() => {
          this.showPopup("Something went wrong");
        })
        .finally(() => {
          this.pausingSubscription = false;
          this.showPauseModal = false;
        });
    },
    resumeSubscription() {
      this.resumingSubscription = true;
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'subscription-resumed' });
      this.subscriptionStore.resumeSubscription(this.subscription)
        .then((result) => {
            this.subscriptionStore.getSubscription(this.route.params.id);
            this.subscriptionStore.loadData();
            this.showPopup(result ? "Subscription Resumed!" : "Something went wrong");
          })
            .catch(() => {
              this.showPopup("Something went wrong");
            })
            .finally(() => {
              this.resumingSubscription = false;
              this.showResumeModal = false;
            });
    },
    showPopup(message) {
      this.popupMessage = message;
      this.$refs.popup.show();
    },
  }
};
</script>

<template>
  <div class="w-full p-4 show-table">
    <div class="bg-white rounded-xl shadow-lg p-6 pb-50">
      <!-- Table Header -->
      <div class="mb-4 border-b pb-2 flex flex-row justify-between">
        <h2 class="font-bold">
          Gift Details
        </h2>

        <div class="flex flex-row gap-2">
          <div
            v-if="subscription && subscription.status === 'active' && subscriptionStore.editing"
            role="button"
            class="btn-filled btn-warning text-sm"
            @click="showWarningModal = true"
          >
            <div class="inline-flex flex-row">
              <div class="mr-1">
                STOP GIFTS
              </div>
            </div>
          </div>
          <div
            v-if="subscription && subscription.status === 'active' && subscriptionStore.editing"
            role="button"
            class="btn-outline text-sm"
            @click="showPauseModal = true"
          >
            PAUSE GIFTS
          </div>
          <div
            v-if="subscription && subscription.status === 'paused' && subscriptionStore.editing"
            role="button"
            class="btn-filled text-sm"
            @click="showResumeModal = true"
          >
            RESUME GIFTS
          </div>
        </div>
      </div>
      <div class="overflow-x-auto">
        <!-- Table Body -->
        <table class="table-body min-w-full border-collapse bg-white">
          <tbody>
            <template v-if="subscriptionStore.loadingRecord">
              <tr
                v-for="i in 8"
                :key="i"
                class="animate-pulse"
              >
                <td class="w-1/2 p-2">
                  <div class="h-4" />
                </td>
                <td class="w-1/2 p-2">
                  <div class="h-4" />
                </td>
              </tr>
            </template>
            <template v-else>
              <tr
                :class="!subscriptionStore.givingBlind ? 'cursor-pointer user-info-row' : ''"
                @click="!subscriptionStore.givingBlind && navigateToDonorView(subscription.donor_member.id)"
              >
                <td>User</td>
                <td>{{ userFullName }}</td>
              </tr>
              <tr>
                <td>Designation</td>
                <td>
                  <!--                TODO-->
                  <!--                <div v-if="subscriptionStore.editing">-->
                  <!--                  <select-->
                  <!--                    id="fund"-->
                  <!--                    v-model="subscriptionStore.localSubscription.fund_id"-->
                  <!--                    class="mt-1 block w-full px-3 py-2 border border-gray-300 shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"-->
                  <!--                  >-->
                  <!--                    <option-->
                  <!--                      v-for="fund in subscriptionStore.funds"-->
                  <!--                      :key="fund.id"-->
                  <!--                      :value="fund.id"-->
                  <!--                    >-->
                  <!--                      {{ fund.name }}-->
                  <!--                    </option>-->
                  <!--                  </select>-->
                  <!--                </div>-->
                  <div>
                    {{ subscription?.donatable_target ? subscription.donatable_target.name : "No Designation" }}
                  </div>
                </td>
              </tr>
              <tr>
                <td>Frequency</td>
                <td> {{ subscriptionStore.intervalString(subscription) }}</td>
              </tr>
              <tr>
                <td>Amount</td>
                <td>{{ ApplicationHelper.formatMoney(subscription.amount) }}</td>
              </tr>
              <tr>
                <td>Payment Method</td>
                <td>{{ ApplicationHelper.formatPaymentProcessorType(subscription.processor_type) }}</td>
              </tr>
              <tr>
                <td>Started On</td>
                <td>{{ ApplicationHelper.formatDate(subscription.created_at, {weekday: false, time: false}) }}</td>
              </tr>
              <tr v-if="subscription.next_payment_date">
                <td v-if="subscription.status === 'trialing'">
                  Donation Start Date
                </td>
                <td v-else>
                  Next Payment Date
                </td>
                <td>{{ ApplicationHelper.formatDate(subscription.next_payment_date, {weekday: false, time: false}) }}</td>
              </tr>
              <tr>
                <td>Status</td>
                <td>{{ t('donation.status.' + [subscription.status]) }}</td>
              </tr>
              <tr>
                <td>Successful Gifts</td>
                <td>{{ subscription.successful_payments_count }}</td>
              </tr>
              <tr>
                <td>Notes</td>
                <td>{{ subscription.notes }}</td>
              </tr>
            </template>
            <tr>
              <th class="pt-4">
                <div class="flex space-x-2">
                  <button
                    class="btn-outline"
                    @click="back"
                  >
                    Back
                  </button>
                  <button
                    v-if="subscription.status === 'active' || subscription.status === 'paused'"
                    :class="subscriptionStore.editing ? 'btn-outline' : 'btn-filled'"
                    @click="toggleEdit"
                  >
                    {{ subscriptionStore.editing ? 'Cancel' : 'Edit' }}
                  </button>
                <!--                <button-->
                <!--                  v-if="subscriptionStore.editing"-->
                <!--                  class="btn-filled"-->
                <!--                  @click="save"-->
                <!--                >-->
                <!--                  Save-->
                <!--                </button>-->
                </div>
              </th>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <section
      class="base-container association-container donations-container mt-4"
    >
      <header>
        <h2 class="body-1-bold">
          Latest Donations
        </h2>
      </header>
      <div class="overflow-container">
        <base-data-table
          :labels="donationData['labels']"
          :rows="donationData['rows']"
          :loading="subscriptionStore.loadingRecord"
          :no-data-message="`No Successful Donations found.`"
          :loading-cells="5"
          :loading-rows="10"
          clickable-rows
          @row-click="(id) => navigateToDonationView(id, 'gift')"
        />
      </div>
    </section>
  </div>

  <base-modal
    v-if="showWarningModal"
    size="small"
    :background-dismissible="!cancellingSubscription"
    @close="showWarningModal = false"
  >
    <template #header-slot>
      <div class="font-semibold text-lg">
        Stop Recurring Gift?
      </div>
    </template>

    <div
      v-if="cancellingSubscription"
      class="flex justify-center items-center"
    >
      <loading-spinner size="icon-large" />
    </div>
    <div v-else>
      <div>
        Are you sure you want to stop the recurring gift for {{ userFullName }}? All further gifts will stop.
      </div>
      <div class="mt-4">
        This action cannot be undone.
      </div>
      <div class="flex flex-row justify-between mt-4">
        <button
          class="btn btn-outline w-1/2 flex justify-center"
          @click="showWarningModal = false"
        >
          GO BACK
        </button>
        <button
          class="btn btn-filled btn-warning ml-2 w-1/2 flex justify-center"
          @click="cancelSubscription"
        >
          STOP GIFTS
        </button>
      </div>
    </div>
  </base-modal>

  <base-modal
    v-if="showPauseModal"
    size="small"
    :background-dismissible="!pausingSubscription"
    @close="showPauseModal = false"
  >
    <template #header-slot>
      <div class="font-semibold text-lg">
        Pause Recurring Gift?
      </div>
    </template>
    <div
      v-if="pausingSubscription"
      class="flex justify-center items-center"
    >
      <loading-spinner size="icon-large" />
    </div>
    <div v-else>
      <div>Pause the recurring gift for {{ userFullName }}? No further gifts will be collected until it is resumed.</div>
      <div class="flex flex-row justify-between mt-4">
        <button
          class="btn btn-outline w-1/2 flex justify-center"
          @click="showPauseModal = false"
        >
          GO BACK
        </button>
        <button
          class="btn btn-filled ml-2 w-1/2 flex justify-center"
          @click="pauseSubscription"
        >
          PAUSE GIFTS
        </button>
      </div>
    </div>
  </base-modal>

  <base-modal
    v-if="showResumeModal"
    size="small"
    :background-dismissible="!resumingSubscription"
    @close="showResumeModal = false"
  >
    <template #header-slot>
      <div class="font-semibold text-lg">
        Resume Recurring Gift?
      </div>
    </template>
    <div
      v-if="resumingSubscription"
      class="flex justify-center items-center"
    >
      <loading-spinner size="icon-large" />
    </div>
    <div v-else>
      <div>Resume the recurring gift for {{ userFullName }}? Gifts will begin collecting again.</div>
      <div class="flex flex-row justify-between mt-4">
        <button
          class="btn btn-outline w-1/2 flex justify-center"
          @click="showResumeModal = false"
        >
          GO BACK
        </button>
        <button
          class="btn btn-filled ml-2 w-1/2 flex justify-center"
          @click="resumeSubscription"
        >
          RESUME GIFTS
        </button>
      </div>
    </div>
  </base-modal>

  <base-modal
    v-if="showStoppedModal"
    size="small"
    @close="showStoppedModal = false"
  >
    <template #header-slot>
      <div class="font-semibold text-lg">
        Recurring Gift Stopped
      </div>
    </template>
    <div>
      <div>
        The recurring gift for {{ userFullName }} has been stopped. No further gifts will be collected.
      </div>
      <div class="mt-4 text-sm text-gray-500">
        This cannot be undone. A stopped gift can't be restarted. The donor would need to set up a new recurring gift.
      </div>
      <div class="flex justify-end mt-4">
        <button
          class="btn btn-filled"
          @click="showStoppedModal = false"
        >
          GOT IT
        </button>
      </div>
    </div>
  </base-modal>

  <popup
    ref="popup"
    :message="popupMessage"
    :duration="1400"
  />
</template>

<style scoped lang="scss">
.table-body {
  tr:nth-child(odd) {
    @apply bg-table_gray;
  }

  tr:nth-child(even) {
    @apply bg-white;
  }

  tr:last-child {
    @apply bg-white;
  }
}

th {
  width: 25%;
  font-size: 10px;
  font-weight: normal;
  text-align: left;
  text-transform: uppercase;
  @apply text-slate-500;
  @apply px-2;
  @apply py-2;
}

td {
  width: 25%;
  font-size: 12px;
  @apply px-2;
  @apply py-4;
}

.user-info-row:hover {
  color: darkcyan;
  background-color: #f1f5f9; /* light gray background on hover */
}

.export-button {
  @apply text-slate-500;
  @apply border border-slate-500;
  border-radius: 5px;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: white;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, border 0.3s;
}

.export-button:hover {
  @apply text-white;
  background-color: #081225;
}

@media screen and (max-width: 720px) {
  .flex {
    flex-direction: row;
    align-items: center;
  }
}
</style>
